import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Avatar from "@/components/Avatar";
import ReportButton from "@/components/ReportButton";
import { Plus, Heart, ExternalLink, X, Loader2, Sparkles, Filter, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_CATEGORIES = [
  "Sermons & Teaching",
  "Worship",
  "Podcasts",
  "Faith Blogs",
  "Christian Tools",
];

function externalDomain(href) {
  try { return new URL(href).hostname.replace(/^www\./, ""); }
  catch { return href; }
}

function ConfirmedLink({ href, children, ...rest }) {
  const onClick = (e) => {
    const ok = window.confirm(
      `Leaving Jesus Just Us — continue to ${externalDomain(href)}?\n\nWe don't control external sites. Make sure the content honors our community covenant.`
    );
    if (!ok) e.preventDefault();
  };
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} {...rest}>{children}</a>
  );
}

export default function Inspiration() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get("category");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(
    initialCategory && DEFAULT_CATEGORIES.includes(initialCategory) ? initialCategory : "All"
  );
  const [openSubmit, setOpenSubmit] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", description: "", category: initialCategory && DEFAULT_CATEGORIES.includes(initialCategory) ? initialCategory : DEFAULT_CATEGORIES[0] });
  const [busy, setBusy] = useState(false);

  // Keep the URL in sync with the active tab so deep-links from the landing page
  // ("Gospel Music" → /inspiration?category=Worship) survive a refresh, and a
  // shared URL always lands the visitor on the same view.
  useEffect(() => {
    if (active === "All") {
      if (searchParams.get("category")) setSearchParams({}, { replace: true });
    } else {
      if (searchParams.get("category") !== active) setSearchParams({ category: active }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const load = async (cat) => {
    setLoading(true);
    try {
      const params = cat && cat !== "All" ? { category: cat } : {};
      const { data } = await api.get("/inspirations", { params });
      setItems(data);
    } catch {
      toast.error("Could not load inspirations");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(active); }, [active]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.url.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/inspirations", form);
      toast.success(data.status === "approved"
        ? "Posted to the wall — thank you!"
        : "Submitted — an admin will review and approve.");
      setForm({ title: "", url: "", description: "", category: DEFAULT_CATEGORIES[0] });
      setOpenSubmit(false);
      load(active);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast.error((status === 422 && detail) ? detail : (detail || "Could not submit"));
    } finally { setBusy(false); }
  };

  const toggleLove = async (it) => {
    try {
      const { data } = await api.post(`/inspirations/${it.id}/love`);
      setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, loves: data.loves, i_love: data.i_love } : x));
    } catch { /* silent */ }
  };

  const isAdmin = user?.role === "admin";
  const deleteInspiration = async (it) => {
    if (!window.confirm(`Remove "${it.title}" from the wall?`)) return;
    try {
      await api.patch(`/admin/inspirations/${it.id}?action=delete`);
      setItems((arr) => arr.filter((x) => x.id !== it.id));
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const tabs = useMemo(() => ["All", ...DEFAULT_CATEGORIES], []);

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="overline text-burgundy flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Inspiration Hub</div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl text-jjblue mt-2 leading-tight">Where the family points its eyes.</h1>
          <p className="mt-2 text-[var(--jj-text-secondary)] max-w-xl">
            Sermons, worship sets, podcasts, blogs, and tools that have stirred members' hearts. Every link
            is reviewed before it goes on the wall.
          </p>
        </div>
        <button onClick={() => setOpenSubmit(true)} className="btn-primary inline-flex items-center gap-2" data-testid="inspo-new">
          <Plus className="h-4 w-4" /> Share a link
        </button>
      </div>

      {/* Category tabs */}
      <div className="mt-6 flex gap-1.5 flex-wrap" data-testid="inspo-tabs">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setActive(t)}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${active === t ? "bg-burgundy text-cream" : "bg-cream-surface text-jjblue/80 hover:text-burgundy"}`}
            data-testid={`inspo-tab-${t.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && (
          <div className="col-span-full text-center text-[var(--jj-muted)] py-10 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-center text-[var(--jj-muted)] py-12">
            Nothing here yet. Be the first to share a link that blessed you.
          </div>
        )}
        {items.map((it) => (
          <div key={it.id} className="jj-card p-5 flex flex-col gap-3" data-testid={`inspo-card-${it.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-burgundy">{it.category}</div>
                <h3 className="font-serif text-xl text-jjblue mt-1 leading-tight truncate">{it.title}</h3>
                <ConfirmedLink
                  href={it.url}
                  className="mt-1 text-xs text-[var(--jj-muted)] hover:text-burgundy inline-flex items-center gap-1 truncate max-w-full"
                  data-testid={`inspo-url-${it.id}`}
                >
                  <ExternalLink className="h-3 w-3 shrink-0" /> <span className="truncate">{externalDomain(it.url)}</span>
                </ConfirmedLink>
              </div>
              <ReportButton targetType="inspiration" targetId={it.id} label="" />
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => deleteInspiration(it)}
                  className="p-1 text-[var(--jj-muted)] hover:text-burgundy transition"
                  title="Delete inspiration"
                  aria-label="Delete inspiration"
                  data-testid={`inspo-delete-${it.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.7} />
                </button>
              )}
            </div>
            {it.description && (
              <p className="text-sm text-jjblue/85 leading-relaxed">{it.description}</p>
            )}
            <div className="flex items-center justify-between text-xs text-[var(--jj-muted)] mt-1">
              <div className="flex items-center gap-2 min-w-0">
                <Avatar user={it.submitter} size={22} />
                <span className="truncate">Shared by {it.submitter?.name || "a member"}</span>
              </div>
              <button
                onClick={() => toggleLove(it)}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 transition ${it.i_love ? "bg-burgundy/10 text-burgundy" : "text-[var(--jj-muted)] hover:text-burgundy"}`}
                data-testid={`inspo-love-${it.id}`}
              >
                <Heart className={`h-3.5 w-3.5 ${it.i_love ? "fill-current" : ""}`} /> {it.loves}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Submit modal */}
      {openSubmit && (
        <div className="fixed inset-0 z-50 bg-jjblue/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpenSubmit(false)}>
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="jj-card w-full max-w-lg p-6 sm:p-7"
            data-testid="inspo-submit-form"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="overline text-burgundy">Share an inspiration</div>
                <h3 className="font-serif text-2xl text-jjblue mt-1">A link that blessed you.</h3>
              </div>
              <button type="button" onClick={() => setOpenSubmit(false)} className="text-[var(--jj-muted)] hover:text-burgundy" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-burgundy">Title</label>
                <input
                  className="input-jj mt-1"
                  placeholder="e.g., Maverick City Worship — Promises"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  maxLength={140}
                  required
                  data-testid="inspo-title"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-burgundy">Link (https://…)</label>
                <input
                  className="input-jj mt-1"
                  placeholder="https://"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  required
                  data-testid="inspo-url-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-burgundy">Why this blessed you</label>
                <textarea
                  className="input-jj mt-1 min-h-[80px]"
                  placeholder="One short sentence — the family will see it."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  maxLength={480}
                  data-testid="inspo-desc"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-burgundy">Category</label>
                <select
                  className="input-jj mt-1"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="inspo-category"
                >
                  {DEFAULT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full mt-5" data-testid="inspo-submit">
              {busy ? "Submitting…" : (user?.role === "admin" ? "Post immediately" : "Submit for review")}
            </button>
            <p className="mt-3 text-[11px] text-[var(--jj-muted)] text-center">
              Links are reviewed against our covenant of inspiring, uplifting, motivational, and honest content.
            </p>
          </form>
        </div>
      )}
    </div>
  );
}
